/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {AssetType, GenericObject} from '@ion/core/src/types/entities';
import BaseSymbol, {
  EndIcon,
  IconGeometryType,
  PointMarkerInterface
} from './BaseSymbols';
import {
  AssetIcon,
  BatteryEmptyIcon,
  BatteryFullIcon,
  BatteryHalfIcon,
  BatteryHighIcon,
  BatteryLowIcon,
  ChargingIcon,
  MachineIcon,
  PoweredIcon,
  ToolIcon,
  UnPoweredIcon
} from './Icons';

const assetIconMap = {
  [AssetType.Machine]   : MachineIcon,
  [AssetType.Trailer]   : UnPoweredIcon,
  [AssetType.Powered]   : PoweredIcon,
  [AssetType.Tool]      : ToolIcon,
  [AssetType.Unpowered] : UnPoweredIcon
};

const BATTERY_EMPTY = 'BATTERY_EMPTY';
const BATTERY_LOW = 'BATTERY_LOW';
const BATTERY_HALF = 'BATTERY_HALF';
const BATTERY_HIGH = 'BATTERY_HIGH';
const BATTERY_FULL = 'BATTERY_FULL';
const CHARGING = 'CHARGING';

export const EndIconType = {
  [BATTERY_EMPTY] : BATTERY_EMPTY,
  [BATTERY_LOW]   : BATTERY_LOW,
  [BATTERY_HALF]  : BATTERY_HALF,
  [BATTERY_HIGH]  : BATTERY_HIGH,
  [BATTERY_FULL]  : BATTERY_FULL,
  [CHARGING]      : CHARGING
};

const endIconMap = {
  [BATTERY_EMPTY] : BatteryEmptyIcon,
  [BATTERY_LOW]   : BatteryLowIcon,
  [BATTERY_HALF]  : BatteryHalfIcon,
  [BATTERY_HIGH]  : BatteryHighIcon,
  [BATTERY_FULL]  : BatteryFullIcon,
  [CHARGING]      : ChargingIcon
};

enum Points {
  ZERO = 0,
  FOUR = 4,
  EIGHT = 8,
  TWELVE = 12,
  SIXTEEN = 16,
  TWENTY = 20,
  TWENTYFOUR = 24,
  TWENTYEIGHT = 28,
}

const createMarkerTypePoints = (values: number[]): GenericObject => {
  return {
    [Points.ZERO]        : values[0],
    [Points.FOUR]        : values[1],
    [Points.EIGHT]       : values[2],
    [Points.TWELVE]      : values[3],
    [Points.SIXTEEN]     : values[4],
    [Points.TWENTY]      : values[5],
    [Points.TWENTYFOUR]  : values[6],
    [Points.TWENTYEIGHT] : values[7]
  };
};

const _batteryMarkerPointXValues = createMarkerTypePoints([-5.5, -7, -9, -11.5, -14.5, -17.8, -21.5, -24]);

/**
 * Function that determines the asset icon based on the asset type.
 * @param asset 
 * @returns Icon with placement position of type IconGeometryType
 * 
 */
const getAssetIcon = (type: AssetType) => {
  return assetIconMap[type] || AssetIcon;
};

interface TitleSymbolsProps {
  title: string;
  isSelected: boolean;
  arrowCircleColor: number[];
  assetType: AssetType;
  endIcon?: {
    type: string;
    color: number[];
  };
}

/**
 * @see [Polygon Points Generator](https://betravis.github.io/shape-tools/path-to-polygon/) for polygon points details
 */
class PointSymbol implements Partial<BaseSymbol> {
  /**
   * @static
   * Currently supported asset types
   */
  private static _assetTypes = [
    AssetType.Machine,
    AssetType.Powered,
    AssetType.Tool,
    AssetType.Unpowered,
    AssetType.Trailer
  ];
  
  private static _endIconTypes = [
    BATTERY_EMPTY,
    BATTERY_LOW,
    BATTERY_HALF,
    BATTERY_HIGH,
    BATTERY_FULL,
    CHARGING
  ];

  /**
   * @static
   * Map containing key/value pair of marker type and list of x co-ordinates depending on the text length.
   * x co-ordinates are used to place the marker on the label along the x-axis.
   */
  private static _markerPointXMap = {
    [AssetType.Asset]     : createMarkerTypePoints([-3, -5, -5.5, -8.5, -15.5, -19, -22, -26]),
    [AssetType.Machine]   : createMarkerTypePoints([1.5, -1, -1, -4.2, -12, -15, -16.5, -22.5]),
    [AssetType.Powered]   : createMarkerTypePoints([1.5, -1, -1, -4.2, -12, -15, -16.5, -22.5]),
    [AssetType.Tool]      : createMarkerTypePoints([1.5, -1, -1, -4.2, -12, -15, -16.5, -22.5]),
    [AssetType.Unpowered] : createMarkerTypePoints([1, -1, -1, -4, -12.5, -15.5, -17.5, -22]),
    [AssetType.Trailer]   : createMarkerTypePoints([1, -1, -1, -4, -12.5, -15.5, -17.5, -22])
  };

  private static _endMarkerPointXMap = {
    [CHARGING]      : createMarkerTypePoints([-4, -5, -7.2, -8.8, -11, -13.5, -16, -18]),
    [BATTERY_EMPTY] : _batteryMarkerPointXValues,
    [BATTERY_LOW]   : _batteryMarkerPointXValues,
    [BATTERY_HALF]  : _batteryMarkerPointXValues,
    [BATTERY_HIGH]  : _batteryMarkerPointXValues,
    [BATTERY_FULL]  : _batteryMarkerPointXValues
  };

  /**
   * @static
   * Map containing key/value pair of label length and x co-ordinates depending on the text length.
   * x co-ordinates are used for the position on label wrapper on x-axis.
   */
  private static _labelWrapperXMaxMap = {
    0  : 42,
    4  : 35,
    8  : 35,
    12 : 25,
    16 : 28,
    20 : 25,
    24 : 22,
    28 : 10
  };

  /**
   * @static
   * Map containing key/value pair of label length and x co-ordinates depending on the text length.
   * x co-ordinates are used for the position of the label text on the x-axis when an end icon is
   * present. 
   */
  private static _textXGeometryMap = {
    0  : -8,
    4  : -7.5,
    8  : -13,
    12 : -12,
    16 : 0,
    20 : 0,
    24 : 0,
    28 : 0
  };

  /**
   * @static
   * Static utility function to provide {x, y} co-ordinates where the asset icon marker needs to be placed.
   * It uses _markerPointXMap based on label and asset type.
   */
  private static _getMarkerAnchorPoint({textLength, x, y, assetType, endIcon}) {
    const point = {x, y};
    const type = PointSymbol._assetTypes.includes(assetType) ? assetType : AssetType.Asset;
    if (textLength >= 28) {
      point.x = PointSymbol._markerPointXMap[type][28];
    }
    if (textLength >= 24 && textLength < 28) {
      point.x = PointSymbol._markerPointXMap[type][24];
    }
    if (textLength >= 20 && textLength < 24) {
      point.x = PointSymbol._markerPointXMap[type][20];
    }
    if (textLength >= 16 && textLength < 20) {
      point.x = PointSymbol._markerPointXMap[type][16];
    }
    if (textLength < 16 && textLength > 12) {
      point.x = PointSymbol._markerPointXMap[type][12] + (endIcon ? -3 : 0);
    }
    if (textLength <= 12 && textLength >= 8) {
      point.x = PointSymbol._markerPointXMap[type][8] + (endIcon ? -3.5 : 0);
    }
    if (textLength < 8 && textLength > 4) {
      point.x = PointSymbol._markerPointXMap[type][4] + (endIcon ? -2 : 0);
    }
    if (textLength <= 4) {
      point.x = PointSymbol._markerPointXMap[type][0] + (endIcon ? -2.5 : 0);
    }
    return point;
  }

  /**
   * @static
   * Static utility function to provide {x, y} co-ordinates where the end icon marker needs to be placed.
   * It uses _endMarkerPointXMap based on label and end icon type.
   */
  private static _getEndMarkerAnchorPoint({textLength, x, y, endIcon}) {
    const point = {x, y};
    const type = PointSymbol._endIconTypes.includes(endIcon.type) ? endIcon.type : BATTERY_EMPTY;
    if (textLength >= 28) {
      point.x = PointSymbol._endMarkerPointXMap[type][28];
    }
    if (textLength >= 24 && textLength < 28) {
      point.x = PointSymbol._endMarkerPointXMap[type][24];
    }
    if (textLength >= 20 && textLength < 24) {
      point.x = PointSymbol._endMarkerPointXMap[type][20];
    }
    if (textLength >= 16 && textLength < 20) {
      point.x = PointSymbol._endMarkerPointXMap[type][16];
    }
    if (textLength < 16 && textLength > 12) {
      point.x = PointSymbol._endMarkerPointXMap[type][12];
    }
    if (textLength <= 12 && textLength >= 8) {
      point.x = PointSymbol._endMarkerPointXMap[type][8];
    }
    if (textLength < 8 && textLength > 4) {
      point.x = PointSymbol._endMarkerPointXMap[type][4];
    }
    if (textLength <= 4) {
      point.x = PointSymbol._endMarkerPointXMap[type][0];
    }
    return point;
  }

  /**
   * @static
   * Static utility function to provide the matrix for label wrapper geometry rings.
   * It uses label length to determine the wrapper position.
   */
  private static _getLabelFrameGeometry(textLength: number, endIcon: EndIcon) {
    if (textLength >= 28) {
      const xPoint = endIcon
        ? 24 - textLength
        : -8;
      return [
        [-19, 34],
        [textLength + xPoint, 34],
        [textLength + xPoint, 30],
        [-19, 30],
        [-19, 34]
      ];
    }
    if (textLength >= 24 && textLength < 28) {
      const xPoint = endIcon
        ? 28.75 - textLength
        : -1.5;
      return [
        [-10, 34],
        [textLength + xPoint, 34],
        [textLength + xPoint, 30],
        [-10, 30],
        [-10, 34]
      ];
    }
    if (textLength >= 20 && textLength < 24) {
      const xPoint = endIcon
        ? 26.5 - textLength
        : 1;
      return [
        [-7, 34],
        [textLength + xPoint, 34],
        [textLength + xPoint, 30],
        [-7, 30],
        [-7, 34]
      ];
    }
    if (textLength >= 16 && textLength < 20) {
      let xPoint = 4;
      if (endIcon) {
        if (textLength === 19) {
          xPoint = 5.75;
        } else if (textLength === 18) {
          xPoint = 7;
        } else if (textLength === 17) {
          xPoint = 8.25;
        } else {
          xPoint = 9;
        }
      }
      return [
        [-4, 34],
        [textLength + xPoint, 34],
        [textLength + xPoint, 30],
        [-4, 30],
        [-4, 34]
      ];
    }
    if (textLength > 12 && textLength < 16) {
      let xPoint = 7;
      if (endIcon) {
        if (textLength === 15) {
          xPoint = 8.5;
        } else if (textLength === 14) {
          xPoint = 9.5;
        } else {
          xPoint = 10.5;
        }
      }
      return [
        [1, 34],
        [textLength + xPoint, 34],
        [textLength + xPoint, 30],
        [1, 30],
        [1, 34]
      ];
    }
    if (textLength >= 8 && textLength <= 12) {
      let xPoint = 12.5;
      if (endIcon) {
        if (textLength === 12) {
          xPoint = 15;
        } else if (textLength === 11) {
          xPoint = 16;
        } else if (textLength === 10) {
          xPoint = 18;
        } else if (textLength === 9) {
          xPoint = 18;
        } else {
          xPoint = 19;
        }
      }
      return [
        [7, 34],
        [textLength + xPoint, 34],
        [textLength + xPoint, 30],
        [7, 30],
        [7, 34]
      ];
    }
    if (textLength > 4 && textLength < 8) {
      const xPoint = endIcon
        ? 24 - textLength
        : 14;
      return [
        [7, 34],
        [textLength + xPoint, 34],
        [textLength + xPoint, 30],
        [7, 30],
        [7, 34]
      ];
    }
    const xPoint = endIcon
      ? 26 - textLength
      : 21;
    return [
      [11, 34],
      [textLength + xPoint, 34],
      [textLength + xPoint, 30],
      [11, 30],
      [11, 34]
    ];
  }

  /**
   * @static
   * Static utility function to provide xMax value in frame object for the label wrapper.
   * It uses label length and whether an end icon is present to determine the value.
   */
  private static _getLabelFrameXMax({textLength, endIcon}) {
    if (textLength >= 28) {
      return PointSymbol._labelWrapperXMaxMap[28] + (endIcon ? -2 : 0);
    }
    if (textLength >= 24 && textLength < 28) {
      return PointSymbol._labelWrapperXMaxMap[24] + (endIcon ? 1.5 : 0);
    }
    if (textLength >= 20 && textLength < 24) {
      return PointSymbol._labelWrapperXMaxMap[20];
    }
    if (textLength >= 16 && textLength < 20) {
      return PointSymbol._labelWrapperXMaxMap[16];
    }
    if (textLength < 16 && textLength > 12) {
      return PointSymbol._labelWrapperXMaxMap[12] + (endIcon ? 5.5 : 0);
    }
    if (textLength <= 12 && textLength >= 8) {
      return PointSymbol._labelWrapperXMaxMap[8] + (endIcon ? 8 : 0);
    }
    if (textLength < 8 && textLength > 4) {
      return PointSymbol._labelWrapperXMaxMap[4] + (endIcon ? 4.5 : 0);
    }
    return PointSymbol._labelWrapperXMaxMap[0] + (endIcon ? 4.5 : 0);
  };

  /**
   * @static
   * Static utility function to provide an x geometry value for the label text. It uses label length
   * and whether an end icon is present to determine the value.
   */
  private static _textXGeometry(textLength: number, endIcon: EndIcon) {
    if (!endIcon) {
      return 0;
    }
    if (textLength >= 28) {
      return PointSymbol._textXGeometryMap[28];
    }
    if (textLength >= 24 && textLength < 28) {
      return PointSymbol._textXGeometryMap[24];
    }
    if (textLength >= 20 && textLength < 24) {
      return PointSymbol._textXGeometryMap[20];
    }
    if (textLength >= 16 && textLength < 20) {
      return PointSymbol._textXGeometryMap[16];
    }
    if (textLength < 16 && textLength > 12) {
      return PointSymbol._textXGeometryMap[12];
    }
    if (textLength <= 12 && textLength >= 8) {
      return PointSymbol._textXGeometryMap[8];
    }
    if (textLength < 8 && textLength > 4) {
      return PointSymbol._textXGeometryMap[4];
    }
    return PointSymbol._textXGeometryMap[0];
  }

  /**
   * @static
   * Static utility function to provide an x offset value for the label text. It uses label length
   * to determine the value.
   */
  private static _textXOffset(textLength: number) {
    if (textLength < 16 && textLength > 12) {
      return 14;
    }
    if (textLength <= 12 && textLength > 8) {
      return textLength + 5;
    }
    if (textLength <= 8 && textLength > 4) {
      return textLength + 2;
    }
    if (textLength <= 4) {
      return 13;
    }
    return 0;
  }

  /**
   * Function that returns label, icons and label wrapper if isLabellingActive is true
   */
  private _getTitleSymbols({
    title,
    isSelected,
    arrowCircleColor,
    assetType,
    endIcon
  }: TitleSymbolsProps) {
    const symbols = [
      this._getLabelPointer(arrowCircleColor),
      this._getMarkerIcon({assetType, isSelected, title, endIcon}),
      this._getLabelTitle(title, isSelected, endIcon),
      this._getLabelWrapper({arrowCircleColor, title, endIcon})
    ];
    if (endIcon) {
      symbols.splice(2, 0, this._getMarkerEndIcon(title, endIcon));
    }
    return title ? symbols : [];
  }

  /**
   * Function that to draw the arrow with rotation
   */
  private _getArrow(heading, arrowColor) {
    return {
      type             : 'CIMVectorMarker',
      primitiveName    : 'arrowRotation',
      enable           : true,
      anchorPoint      : {x: 1.5, y: 1},
      anchorPointUnits : 'Relative',
      rotateClockwise  : true,
      rotation         : heading,
      size             : 3.5,
      frame            : {
        xmin : 0,
        ymin : 0,
        xmax : 4,
        ymax : 4
      },
      markerGraphics : [
        {
          type     : 'CIMMarkerGraphic',
          geometry : {
            rings : [
              [
                [6.617, 13.338],
                [0.472, 1.770],
                [1.803, 0.407],
                [7.051, 3.041],
                [7.949, 3.041],
                [13.197, 0.407],
                [14.528, 1.770],
                [8.383, 13.338],
                [6.617, 13.338]
              ]
            ]
          },
          symbol : {
            type         : 'CIMPolygonSymbol',
            symbolLayers : [
              {
                type          : 'CIMSolidFill',
                primitiveName : 'arrowColor',
                enable        : true,
                color         : [...arrowColor, 255]
              }
            ]
          }
        }
      ],
      scaleSymbolsProportionally : true,
      respectFrame               : true
    };
  }

  /**
   * Function that to draw the circle around the arrow
   */
  private _getArrowCircle(arrowCircleColor: number[]) {
    return {
      type             : 'CIMVectorMarker',
      enable           : true,
      anchorPoint      : {x: 1, y: 1},
      anchorPointUnits : 'Relative',
      size             : 6,
      frame            : {xmin: 0.0, ymin: 0.0, xmax: 6.0, ymax: 6.0},
      markerGraphics   : [
        {
          type     : 'CIMMarkerGraphic',
          geometry : {
            rings : [
              [
                [8.5, 0.2],
                [7.06, 0.33],
                [5.66, 0.7],
                [4.35, 1.31],
                [3.16, 2.14],
                [2.14, 3.16],
                [1.31, 4.35],
                [0.7, 5.66],
                [0.33, 7.06],
                [0.2, 8.5],
                [0.33, 9.94],
                [0.7, 11.34],
                [1.31, 12.65],
                [2.14, 13.84],
                [3.16, 14.86],
                [4.35, 15.69],
                [5.66, 16.3],
                [7.06, 16.67],
                [8.5, 16.8],
                [9.94, 16.67],
                [11.34, 16.3],
                [12.65, 15.69],
                [13.84, 14.86],
                [14.86, 13.84],
                [15.69, 12.65],
                [16.3, 11.34],
                [16.67, 9.94],
                [16.8, 8.5],
                [16.67, 7.06],
                [16.3, 5.66],
                [15.69, 4.35],
                [14.86, 3.16],
                [13.84, 2.14],
                [12.65, 1.31],
                [11.34, 0.7],
                [9.94, 0.33],
                [8.5, 0.2]
              ]
            ]
          },
          symbol : {
            type         : 'CIMPolygonSymbol',
            symbolLayers : [
              {
                type        : 'CIMSolidStroke',
                enable      : true,
                capStyle    : 'Round',
                joinStyle   : 'Round',
                lineStyle3D : 'Strip',
                miterLimit  : 10,
                width       : 0.8,
                color       : [167, 169, 172, 255]
              },
              {
                type   : 'CIMSolidFill',
                enable : true,
                color  : [...arrowCircleColor, 255]
              }
            ]
          }
        }
      ],
      scaleSymbolsProportionally : true,
      respectFrame               : true
    };
  }

  /**
   * Function that to draw the the pointer that exists between marker and the label
   */
  private _getLabelPointer(arrowCircleColor: number[]) {
    return {
      type               : 'CIMVectorMarker',
      enable             : true,
      anchorPoint        : {x: 0, y: 10, z: 0},
      dominantSizeAxis3D : 'Y',
      size               : 4,
      rotation           : 180,
      billboardMode3D    : 'FaceNearPlane',
      frame              : {xmin: 0, ymin: 0, xmax: 18, ymax: 11},
      markerGraphics     : [
        {
          type     : 'CIMMarkerGraphic',
          geometry : {
            rings : [
              [[7.91243, 9.95585], [1.04434, 2.17406], [1.62421, 0.22442],
                [2.67116, 0], [16.4073, 0], [18.3419, 1.41066], [18.0342, 2.17406],
                [11.1661, 9.95585], [8.4923, 10.3787], [7.91243, 9.95585]]
            ]
          },
          symbol : {
            type         : 'CIMPolygonSymbol',
            symbolLayers : [
              {
                type        : 'CIMSolidStroke',
                enable      : true,
                capStyle    : 'Round',
                joinStyle   : 'Round',
                lineStyle3D : 'Strip',
                miterLimit  : 2,
                width       : 0.5,
                color       : [167, 169, 172, 255]
              },
              {
                type   : 'CIMSolidFill',
                enable : true,
                color  : [...arrowCircleColor, 255]
              }
            ]
          }
        }
      ],
      scaleSymbolsProportionally : true,
      respectFrame               : true
    };
  }

  /**
   * Function that to draw the asset title
   */
  private _getLabelTitle(title: string, isSelected: boolean, endIcon: EndIcon) {
    return {
      type             : 'CIMVectorMarker',
      enable           : true,
      anchorPoint      : {x: 0, y: -0.5},
      anchorPointUnits : 'Relative',
      size             : 3.5,
      colorLocked      : true,
      frame            : {xmin: 0, ymin: 0, xmax: 5, ymax: 5},
      markerGraphics   : [
        {
          type     : 'CIMMarkerGraphic',
          geometry : {
            x : PointSymbol._textXGeometry(title.length, endIcon),
            y : 30
          },
          symbol : {
            type                : 'CIMTextSymbol',
            fontFamilyName      : 'Roboto Condensed Light',
            fontStyleName       : 'Bold',
            height              : 10,
            horizontalAlignment : 'Center',
            offsetX             : PointSymbol._textXOffset(title.length),
            offsetY             : 0,
            symbol              : {
              type         : 'CIMPolygonSymbol',
              symbolLayers : [
                {
                  type   : 'CIMSolidFill',
                  enable : true,
                  color  : isSelected ? [255, 255, 255, 255] : [0, 0, 0, 255]
                }
              ]
            },
            verticalAlignment : 'Center'
          },
          textString : title
        }
      ],
      scaleSymbolsProportionally : true,
      respectFrame               : true
    };
  }

  /**
   * Function that to draw the marker icon.
   */
  private _getMarkerIcon({
    assetType,
    isSelected,
    title,
    endIcon
  }) {
    const titleLength = title?.length ?? 0;
    const assetIcon: IconGeometryType = getAssetIcon(assetType);
    const {anchorPoint, icon} = assetIcon;
    const {x, y} = PointSymbol._getMarkerAnchorPoint({
      textLength : titleLength,
      x          : anchorPoint.x,
      y          : anchorPoint.y,
      assetType,
      endIcon
    });
    return {
      type               : 'CIMVectorMarker',
      enable             : true,
      anchorPointUnits   : 'Relative',
      dominantSizeAxis3D : 'Y',
      size               : 3,
      rotation           : 180,
      anchorPoint        : {x, y},
      billboardMode3D    : 'FaceNearPlane',
      frame              : {
        xmin : 0,
        ymin : 0,
        xmax : 5,
        ymax : 5
      },
      markerGraphics : [
        {
          type     : 'CIMMarkerGraphic',
          geometry : {
            rings : icon
          },
          symbol : {
            type         : 'CIMPolygonSymbol',
            symbolLayers : [
              {
                type        : 'CIMSolidStroke',
                enable      : true,
                capStyle    : 'Round',
                joinStyle   : 'Round',
                lineStyle3D : 'Strip',
                miterLimit  : 10,
                width       : 0,
                color       : isSelected ? [255, 255, 255, 255] : [110, 110, 110, 170]
              },
              {
                type   : 'CIMSolidFill',
                enable : true,
                color  : isSelected ? [255, 255, 255, 255] : [51, 51, 51, 170]
              }
            ]
          }
        }
      ],
      scaleSymbolsProportionally : true,
      respectFrame               : true
    };
  }

  private _getMarkerEndIcon(title: string, endIconInfo: EndIcon) {
    const titleLength = title?.length ?? 0;
    const endIcon: IconGeometryType = endIconMap[endIconInfo.type];
    const {anchorPoint, icon} = endIcon;
    const {x, y} = PointSymbol._getEndMarkerAnchorPoint({
      textLength : titleLength,
      x          : anchorPoint.x,
      y          : anchorPoint.y,
      endIcon    : endIconInfo
    });
    return {
      type               : 'CIMVectorMarker',
      enable             : true,
      anchorPointUnits   : 'Relative',
      dominantSizeAxis3D : 'Y',
      size               : endIcon === ChargingIcon ? 4 : 3,
      anchorPoint        : {x, y},
      billboardMode3D    : 'FaceNearPlane',
      frame              : {
        xmin : 0,
        ymin : 0,
        xmax : 5,
        ymax : 5
      },
      markerGraphics : [
        {
          type     : 'CIMMarkerGraphic',
          geometry : {
            rings : icon
          },
          symbol : {
            type         : 'CIMPolygonSymbol',
            symbolLayers : [
              {
                type        : 'CIMSolidStroke',
                enable      : true,
                capStyle    : 'Round',
                joinStyle   : 'Round',
                lineStyle3D : 'Strip',
                miterLimit  : 10,
                width       : 0,
                color       : [...endIconInfo.color, 255]
              },
              {
                type   : 'CIMSolidFill',
                enable : true,
                color  : [...endIconInfo.color, 255]
              }
            ]
          }
        }
      ],
      scaleSymbolsProportionally : true,
      respectFrame               : true
    };
  }

  /**
   * Function that to draw the label wrapper
   */
  private _getLabelWrapper({
    arrowCircleColor,
    title,
    endIcon
  }) {
    const titleLength = title?.length ?? 0;
    return {
      type               : 'CIMVectorMarker',
      enable             : true,
      anchorPoint        : {x: -0.1, y: -1.1},
      anchorPointUnits   : 'Relative',
      dominantSizeAxis3D : 'Y',
      size               : 20,
      billboardMode3D    : 'FaceNearPlane',
      frame              : {
        xmin : 0,
        ymin : 30,
        xmax : PointSymbol._getLabelFrameXMax({textLength: titleLength, endIcon}),
        ymax : 35
      },
      markerGraphics : [
        {
          type     : 'CIMMarkerGraphic',
          geometry : {
            rings : [
              PointSymbol._getLabelFrameGeometry(titleLength, endIcon)
            ]
          },
          symbol : {
            type         : 'CIMPolygonSymbol',
            symbolLayers : [
              {
                type        : 'CIMSolidStroke',
                enable      : true,
                capStyle    : 'Round',
                joinStyle   : 'Round',
                lineStyle3D : 'Strip',
                miterLimit  : 10,
                width       : 0.2,
                color       : [167, 169, 172, 255]
              },
              {
                type   : 'CIMSolidFill',
                enable : true,
                color  : [...arrowCircleColor, 255]
              }
            ]
          }
        }
      ],
      scaleSymbolsProportionally : true,
      respectFrame               : true
    };
  }

  createPointMarker({
    arrowColor,
    arrowCircleColor,
    title,
    heading,
    isSelected,
    assetType = AssetType.Asset,
    isLabellingActive = false,
    endIcon = null
  }: PointMarkerInterface) {
    if (isLabellingActive && title) {
      return {
        type     : 'CIMPointSymbol',
        haloSize : 2,
        callout  : {
          leaderTolerance : 2,
          leaderOffset    : 2,
          backgroundScale : 'PropUniform'
        },
        symbolLayers : [
          this._getArrow(heading, arrowColor),
          this._getArrowCircle(arrowCircleColor),
          ...this._getTitleSymbols({
            title : title?.toUpperCase(),
            isSelected,
            arrowCircleColor,
            assetType,
            endIcon
          })
        ]
      };
    }
    return {
      type         : 'CIMPointSymbol',
      symbolLayers : [
        this._getArrow(heading, arrowColor),
        this._getArrowCircle(arrowCircleColor)
      ]
    };
  }
}

export default new PointSymbol();
